import DUIField from "@/components/common/dui-field";
import PhoneField from "@/components/common/phone-field";
import SelectField from "@/components/common/select";
import useRoleCatalog from "@/hooks/catalogs/roles";
import { useAuthorization } from "@/hooks/use-authorization";
import type { NewUserFormData } from "@/schemas/users/new-user";
import { Permissions } from "@/utils/permissions";
import { Badge, Heading, TextField } from "@elaniintc/goes-design-system";
import { Controller, useFormContext } from "react-hook-form";
import DeactivateModal from "./deactivate-modal";
import { useParams } from "react-router";
import { useState, useMemo } from "react";
import useUserPositionCatalog from "@/hooks/catalogs/user-position";
import useUserOrganizationCatalog from "@/hooks/catalogs/organization";
import useUserUnitCatalog from "@/hooks/catalogs/unit";

interface Props {
  isUserDeactivated?: boolean;
  setSelectedRole: (role: { label: string; type: string } | null) => void;
  setSelectedUnit: (
    unit: {
      label: string;
      value: string;
    } | null,
  ) => void;
  setSelectedOrganization: (
    organization: {
      label: string;
      value: string;
    } | null,
  ) => void;
  setSelectedPosition: (
    position: {
      label: string;
      value: string;
    } | null,
  ) => void;
  selectedOrganization: {
    label: string;
    value: string;
  } | null;
  selectedPosition: {
    label: string;
    value: string;
  } | null;
  onSubmit: (data: NewUserFormData, description: string) => void;
}

const Form = ({
  isUserDeactivated,
  setSelectedPosition,
  setSelectedRole,
  setSelectedUnit,
  setSelectedOrganization,
  selectedPosition,
  selectedOrganization,
  onSubmit,
}: Props) => {
  const { id } = useParams();
  const { hasPermission } = useAuthorization();
  const canAssignRole = hasPermission(Permissions.users["assign-role"]);
  const canReadRole = hasPermission(Permissions.roles.read);
  const canUseRole = canAssignRole && canReadRole;

  const {
    roles,
    loadMore: loadMoreRoles,
    isLoading: isLoadingRoles,
    isFetching: isFetchingRoles,
    hasMore: hasMoreRoles,
  } = useRoleCatalog(canUseRole);

  const { position } = useUserPositionCatalog(false);
  const { organization } = useUserOrganizationCatalog(false);
  const { unit } = useUserUnitCatalog();

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    register,
    watch,
    trigger,
  } = useFormContext<NewUserFormData>();

  const [role_id, unit_id, organization_id, position_id] = watch([
    "role_id",
    "department_id",
    "assigned_institution_id",
    "registered_position_id",
  ]);

  const [modalState, setModalState] = useState<
    "idle" | "deactivate-warning" | "deprecated-position"
  >("idle");

  const positionOptions = useMemo(() => {
    const options = position
      .filter((p) => p.active || p.value === position_id)
      .map((p) => ({
        ...p,
        icon: !p.active ? (
          <Badge variant="warning" className="h-6">
            Deprecado
          </Badge>
        ) : undefined,
        iconPosition: "right",
      }));

    if (
      selectedPosition &&
      !options.find((p) => p.value === selectedPosition.value)
    ) {
      const posInCatalog = position.find(
        (p) => p.value === selectedPosition.value,
      );
      return [
        ...options,
        {
          label: selectedPosition.label,
          value: selectedPosition.value,
          active: posInCatalog?.active ?? true,
          icon:
            posInCatalog && !posInCatalog.active ? (
              <Badge variant="warning" className="h-6">
                Deprecado
              </Badge>
            ) : undefined,
          iconPosition: "right",
        },
      ];
    }

    return options;
  }, [position, position_id, selectedPosition]);

  const organizationOptions = useMemo(() => {
    const options = organization
      .filter((o) => o.active || o.value === organization_id)
      .map((o) => ({
        ...o,
        icon: !o.active ? (
          <Badge variant="warning" className="h-6">
            Deprecado
          </Badge>
        ) : undefined,
        iconPosition: "right",
      }));

    if (
      selectedOrganization &&
      !options.find((o) => o.value === selectedOrganization.value)
    ) {
      const orgInCatalog = organization.find(
        (o) => o.value === selectedOrganization.value,
      );
      return [
        ...options,
        {
          label: selectedOrganization.label,
          value: selectedOrganization.value,
          active: orgInCatalog?.active ?? true,
          icon:
            orgInCatalog && !orgInCatalog.active ? (
              <Badge variant="warning" className="h-6">
                Deprecado
              </Badge>
            ) : undefined,
          iconPosition: "right",
        },
      ];
    }

    return options;
  }, [organization, organization_id, selectedOrganization]);

  //TODO: Implement create user logic
  const [tempData, setTempData] = useState<NewUserFormData | null>(null);
  const handlePreSummit = (data: NewUserFormData) => {
    const selectedPos = positionOptions.find((p) => p.value === position_id);
    const selectedOrg = organizationOptions.find(
      (o) => o.value === organization_id,
    );

    if (selectedPos && !selectedPos.active) {
      trigger("registered_position_id");
      return;
    }

    if (selectedOrg && !selectedOrg.active) {
      trigger("assigned_institution_id");
      return;
    }

    if (isUserDeactivated) {
      setTempData(data);
      setModalState("deactivate-warning");
      return;
    }
    onSubmit(data, "");
  };

  return (
    <>
      <DeactivateModal
        isOpen={modalState === "deactivate-warning"}
        onClose={() => setModalState("idle")}
        userId={id || null}
        onAccept={({ description }) => {
          if (tempData) {
            onSubmit(tempData, description);
          }
          setModalState("idle");
        }}
      />
      <form
        className="flex flex-col gap-6 border rounded-lg bg-neutral-object p-6 border-neutral-quiet"
        onSubmit={handleSubmit(handlePreSummit)}
        id="new-user-form"
      >
        <div className="flex flex-col gap-4 @container">
          <Heading variant="h5">Información personal</Heading>
          <div className="flex flex-col gap-4">
            <TextField
              id="first-name"
              label="Nombres"
              placeholder="Nombres"
              required
              error={errors.name?.message}
              {...register("name")}
            />
            <TextField
              id="last-name"
              label="Apellidos"
              placeholder="Apellidos"
              required
              error={errors.last_name?.message}
              {...register("last_name")}
            />
            <Controller
              control={control}
              name="dui"
              render={({ field: { ref, ...rest } }) => {
                return (
                  <DUIField
                    {...rest}
                    id="dui-number"
                    label="Número de DUI"
                    placeholder="00000000-0"
                    required
                    formRef={ref}
                    error={errors.dui?.message}
                  />
                );
              }}
            />
          </div>
        </div>
        <div className="w-full h-px border-t border-neutral-quiet" />
        <div className="flex flex-col gap-4 @container">
          <Heading variant="h5">Información de contacto</Heading>
          <div className="flex flex-col gap-4">
            <TextField
              id="email"
              label="Correo electrónico"
              placeholder="Correo electrónico"
              required
              error={errors.email?.message}
              {...register("email")}
            />
            <Controller
              name="phone_number"
              control={control}
              render={({ field: { ref, ...rest } }) => {
                return (
                  <PhoneField
                    {...rest}
                    id="phone-number"
                    formRef={ref}
                    label="Número de teléfono"
                    placeholder="0000 0000"
                    error={errors.phone_number?.message}
                    required
                  />
                );
              }}
            />
          </div>
        </div>
        <div className="w-full h-px border-t border-neutral-quiet" />
        <div className="flex flex-col gap-4 @container">
          <Heading variant="h5">Otra información</Heading>
          <div className="flex flex-col gap-4">
            <SelectField
              label="Institución asignada"
              placeholder="Institución asignada"
              name="assigned_institution_id"
              required
              value={organization_id}
              options={organizationOptions}
              onChange={(value) => {
                setValue("assigned_institution_id", value);
                trigger("assigned_institution_id");
                const org = organizationOptions.find((o) => o.value === value);
                setSelectedOrganization(org ?? null);
              }}
              isLoading={false}
            />
            <SelectField
              label="Unidad o departamento"
              placeholder="Unidad o departamento"
              name="department_id"
              required
              value={unit_id}
              options={unit}
              onChange={(value) => {
                setValue("department_id", value);
                trigger("department_id");
                const unit_ = unit.find((u) => u.value === value);
                setSelectedUnit(unit_ ?? null);
              }}
              isLoading={false}
            />
            <SelectField
              label="Cargo registrado"
              placeholder="Cargo registrado"
              name="registered_position_id"
              required
              value={position_id}
              options={positionOptions}
              onChange={(value) => {
                setValue("registered_position_id", value);
                trigger("registered_position_id");
                const pos = positionOptions.find((p) => p.value === value);
                setSelectedPosition(pos ?? null);
              }}
              isLoading={false}
            />
          </div>
        </div>
        {canUseRole && (
          <>
            <div className="w-full h-px border-t border-neutral-quiet" />
            <div className="flex flex-col gap-4 @container">
              <Heading variant="h5">Configuración</Heading>
              <div className="flex flex-col gap-4">
                <SelectField
                  label="Rol asignado"
                  placeholder="Rol asignado"
                  name="role_id"
                  value={role_id || ""}
                  options={[...roles]}
                  onChange={(value) => {
                    setValue("role_id", value);
                    trigger("role_id");
                    const role = roles.find((r) => r.value === value);
                    setSelectedRole(
                      role
                        ? {
                            label: role.label,
                            type: role.type,
                          }
                        : null,
                    );
                  }}
                  hasMore={hasMoreRoles}
                  onLoadMore={loadMoreRoles}
                  isDisabled={isLoadingRoles}
                  isLoading={isLoadingRoles || isFetchingRoles}
                />
              </div>
            </div>
          </>
        )}
      </form>
    </>
  );
};

export default Form;
